import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { ProdutoTabelaEspecial } from '../../models/produtos/produto-tabela-especial';
import { PromoLog } from '../../models/produtos/promo-log';
import { ImportacaoPromocaoLinha } from '../../models/produtos/importacao-promocao-linha';
import { CotaPromocaoUsoLog } from '../../models/produtos/cota-promocao-uso-log';

export interface PromocaoListFilters {
  descricao: string;
  ativo: boolean | null;
  tabelaEspecial: boolean | null;
}

export interface PromocoesPagedResult {
  items: ProdutoTabelaEspecial[];
  total: number;
  page: number;
  pageSize: number;
}

@Injectable({ providedIn: 'root' })
export class PromocoesService {
  private baseUrl: string;
  private headers = new HttpHeaders();

  constructor(
    private http: HttpClient,
    @Inject('BASE_URL') baseUrl: string,
    private toastr: ToastrService
  ) {
    this.baseUrl = baseUrl;
  }

  searchPaged(filtros: PromocaoListFilters, page: number, pageSize: number): Observable<PromocoesPagedResult> {
    this.headers.set('Content-Type', 'application/json');

    const data: any = {
      guidclientenapis: localStorage.getItem('guidclientenapis'),
      page,
      pageSize
    };

    if (filtros.descricao && filtros.descricao.trim().length > 0) {
      data.descricao = filtros.descricao.trim();
    }
    if (filtros.ativo !== null) {
      data.ativo = filtros.ativo;
    }
    if (filtros.tabelaEspecial !== null) {
      data.tabelaEspecial = filtros.tabelaEspecial;
    }

    return this.http.post<PromocoesPagedResult>(
      this.baseUrl + 'api/Produto/SearchPromocoesPaged',
      data,
      { headers: this.headers }
    ).pipe(
      catchError(() => {
        this.toastr.error('', 'Houve um erro, contate o suporte...');
        return of({ items: [], total: 0, page, pageSize });
      })
    );
  }

  getPromoLog(): Observable<PromoLog[]> {
    const data = { guidclientenapis: localStorage.getItem('guidclientenapis') };
    return this.http.post<PromoLog[]>(
      this.baseUrl + 'api/Produto/GetPromoLog',
      data,
      { headers: this.headers }
    ).pipe(
      catchError(() => {
        this.toastr.error('', 'Houve um erro, contate o suporte...');
        return of([]);
      })
    );
  }

  /** Baixa a planilha-modelo (template) de importação de promoções. */
  downloadTemplatePromocao(): Observable<Blob> {
    return this.http.get(
      this.baseUrl + 'api/Produto/DownloadTemplatePromocao',
      { responseType: 'blob' }
    );
  }

  /**
   * Envia a planilha preenchida e recebe o resultado linha a linha (itens calculados ou erros).
   * Não persiste nada — a gravação ocorre no fluxo normal de salvar a promoção.
   */
  importarPlanilhaPromocao(arquivo: File): Observable<ImportacaoPromocaoLinha[]> {
    const formData = new FormData();
    formData.append('arquivo', arquivo);
    formData.append('guidclientenapis', localStorage.getItem('guidclientenapis'));

    return this.http.post<ImportacaoPromocaoLinha[]>(
      this.baseUrl + 'api/Produto/ImportarPlanilhaPromocao',
      formData
    );
  }

  getPromoLogById(promo_id: number): Observable<PromoLog[]> {
    const data = { promo_id, guidclientenapis: localStorage.getItem('guidclientenapis') };
    return this.http.post<PromoLog[]>(
      this.baseUrl + 'api/Produto/GetPromoLogById',
      data,
      { headers: this.headers }
    ).pipe(
      catchError(() => {
        this.toastr.error('', 'Houve um erro, contate o suporte...');
        return of([]);
      })
    );
  }

  // Log individual de uso de UM item da promoção (por idprodutoespecial).
  getUsoCotaItem(idprodutoespecial: number): Observable<CotaPromocaoUsoLog[]> {
    const data = { idprodutoespecial, guidclientenapis: localStorage.getItem('guidclientenapis') };
    return this.http.post<CotaPromocaoUsoLog[]>(
      this.baseUrl + 'api/Produto/GetUsoCotaItem',
      data,
      { headers: this.headers }
    ).pipe(
      catchError(() => {
        this.toastr.error('', 'Houve um erro ao carregar o log de uso, contate o suporte...');
        return of([]);
      })
    );
  }

  // Diagnóstico de uso da promoção inteira, com filtro opcional por vendedor e cliente.
  getDiagnosticoUsoPromocao(idprodutotabelaespecial: number, seller_id: number | null, client_id: number | null): Observable<CotaPromocaoUsoLog[]> {
    const data = {
      idprodutotabelaespecial,
      seller_id: seller_id,
      client_id: client_id,
      guidclientenapis: localStorage.getItem('guidclientenapis')
    };
    return this.http.post<CotaPromocaoUsoLog[]>(
      this.baseUrl + 'api/Produto/GetDiagnosticoUsoPromocao',
      data,
      { headers: this.headers }
    ).pipe(
      catchError(() => {
        this.toastr.error('', 'Houve um erro ao carregar o diagnóstico, contate o suporte...');
        return of([]);
      })
    );
  }
}
